import { ResultOf } from "gql.tada";
import { HeroById } from "@queries/entries/HeroById";
import { useTheme } from "@hooks/useTheme";
import { useAnimate, useInView, useScroll, useTransform } from "motion/react";
import { useEffect, useRef } from "react";
import { usePaletteValues } from "@hooks/usePaletteValues";
import { animationCurves } from "@utils/animationCurves";
import { MediaItem } from "@components/shared/MediaItem";

export const ImageAutoHeightHero = ({
  entry: { hero: entry },
}: {
  entry: ResultOf<typeof HeroById>;
}) => {
  const sectionRef = useRef(null);

  const { theme, backgroundColor } = useTheme();
  const paletteValues = usePaletteValues(theme.palette);
  const [, animate] = useAnimate();

  const inView = useInView(sectionRef);
  const { scrollYProgress: sectionExitProgress } = useScroll({
    target: sectionRef,
    offset: ["end end", "end start"],
  });
  const backgroundTransitionBackToDefault = useTransform(
    sectionExitProgress,
    [0, 1],
    [paletteValues.deep(), paletteValues[theme.background]()]
  );

  useTransform(() => {
    const scroll = sectionExitProgress.get();
    if (scroll === 0 || scroll === 1) return;
    backgroundColor.set(backgroundTransitionBackToDefault.get());
  });

  useEffect(() => {
    backgroundColor.animation?.cancel();
    animate(
      backgroundColor,
      inView
        ? backgroundTransitionBackToDefault.get()
        : paletteValues[theme.background](),
      animationCurves.default
    );
  }, [
    animate,
    theme,
    backgroundColor,
    paletteValues,
    backgroundTransitionBackToDefault,
    inView,
  ]);

  let aspectRatio = 'auto';

  if (entry?.backgroundImage) {
    const { width, height } = entry.backgroundImage as any;

    if (width && height) {
      aspectRatio = `${width}/${height}`;
    }
  }

  return (
    <section
      ref={sectionRef}
      className="container-base pt-20 dsk:pt-16 pb-4"
    >
      <div className="size-full relative flex flex-col" style={{ aspectRatio: aspectRatio }}>
        {entry?.backgroundImage && (
          <MediaItem
            alt={entry?.title as string}
            aria-hidden
            item={entry?.backgroundImage}
            className="rounded-xl"
          />
        )}
      </div>
    </section>
  );
};
